import React from 'react';
import { Button, Select } from 'antd';
import { useGetFacetsQuery, type ExploreFilters } from 'services/exploreService';
import { AgeRange } from 'components/explore/AgeRange';

const CohortFilters: React.FC<{
  filters: ExploreFilters;
  update: (patch: Partial<ExploreFilters>) => void;
  clear: () => void;
  activeCount: number;
}> = ({ filters, update, clear, activeCount }) => {
  const { data: facets, isLoading, error, refetch } = useGetFacetsQuery();
  const common = {
    allowClear: true,
    loading: isLoading,
    showSearch: true,
    optionFilterProp: 'label' as const,
    popupMatchSelectWidth: false,
  };
  const models = (facets?.models ?? []).filter(
    (m, index, all) =>
      (!filters.manufacturer || m.manufacturer === filters.manufacturer) &&
      all.findIndex(
        other =>
          other.value === m.value &&
          (!filters.manufacturer || other.manufacturer === filters.manufacturer)
      ) === index
  );
  return (
    <>
      <div className="ax-filters" role="search" aria-label="Filter linked reports">
        <Select
          {...common}
          aria-label="Datasource"
          placeholder="Source"
          value={filters.datasource}
          onChange={value => update({ datasource: value })}
          options={(facets?.datasources ?? []).map(d => ({
            value: d.value,
            label: d.label || d.value,
          }))}
        />
        <Select
          {...common}
          aria-label="Manufacturer"
          placeholder="Manufacturer"
          value={filters.manufacturer}
          onChange={value => update({ manufacturer: value, model: undefined })}
          options={(facets?.manufacturers ?? []).map(d => ({ value: d.value, label: d.value }))}
        />
        <Select
          {...common}
          aria-label="Scanner model"
          placeholder="Scanner model"
          value={filters.model}
          onChange={value => update({ model: value })}
          options={models.map(d => ({ value: d.value, label: d.value }))}
        />
        <Select
          {...common}
          aria-label="Visit year"
          placeholder="Visit year"
          value={filters.year}
          onChange={value => update({ year: value })}
          options={(facets?.years ?? []).map(d => ({ value: d.value, label: d.value }))}
        />
        <AgeRange filters={filters} update={update} />
        {activeCount > 0 && (
          <Button type="link" onClick={clear}>
            Clear filters
          </Button>
        )}
      </div>
      <p className="ax-faint">
        Age is in years at the study visit. Machine filters require a matching active DICOM in that
        study; manufacturer and model must match the same DICOM. Unknown ages are excluded only when
        an age limit is applied.
      </p>
      {error && (
        <p role="alert">
          Could not load source and machine options.{' '}
          <Button onClick={() => refetch()}>Retry filter options</Button>
        </p>
      )}
    </>
  );
};
export default CohortFilters;
